/**
 * PathNavigator Service - Zustand Store
 * Single Responsibility: Handle JSON path operations
 */

import { create } from 'zustand';

export interface PathNavigatorStore {
  /**
   * Get value at JSON path
   * @example getValue({ a: { b: 1 } }, 'a.b') → 1
   */
  getValue: (obj: any, path: string) => any;

  /**
   * Set value at JSON path (creates intermediate objects)
   * @example setValue({}, 'a.b', 1) → { a: { b: 1 } }
   */
  setValue: (obj: any, path: string, value: any) => void;

  /**
   * Create path if missing (idempotent)
   * @example createPath({}, 'a.b.c', {}) → { a: { b: { c: {} } } }
   */
  createPath: (obj: any, path: string, initialValue?: any) => any;

  /**
   * Check if path exists
   */
  hasPath: (obj: any, path: string) => boolean;

  /**
   * Get all available paths in object
   */
  getPaths: (obj: any, maxDepth?: number) => string[];

  /**
   * Delete property at path
   */
  deletePath: (obj: any, path: string) => void;
}

/**
 * Helper function to parse path into segments
 */
function parsePath(path: string): string[] {
  return path
    .split(/[.\[\]]/)
    .filter((segment) => segment.length > 0);
}

export const usePathNavigatorStore = create<PathNavigatorStore>((set, get) => ({
  getValue: (obj: any, path: string): any => {
    const keys = parsePath(path);
    return keys.reduce((current, key) => current?.[key], obj);
  },

  setValue: (obj: any, path: string, value: any): void => {
    const keys = parsePath(path);
    const lastKey = keys.pop();
    if (!lastKey) return;

    const target = keys.reduce((current, key) => {
      if (!(key in current) || typeof current[key] !== 'object') {
        current[key] = {};
      }
      return current[key];
    }, obj);

    target[lastKey] = value;
  },

  createPath: (obj: any, path: string, initialValue: any = {}): any => {
    const keys = parsePath(path);
    let current = obj;

    keys.forEach((key, idx) => {
      if (!(key in current)) {
        const isLast = idx === keys.length - 1;
        current[key] = isLast ? initialValue : {};
      }
      current = current[key];
    });

    return obj;
  },

  hasPath: (obj: any, path: string): boolean => {
    try {
      const keys = parsePath(path);
      const value = keys.reduce((current, key) => current?.[key], obj);
      return value !== undefined;
    } catch {
      return false;
    }
  },

  getPaths: (obj: any, maxDepth: number = 5): string[] => {
    const paths: string[] = [];

    const traverse = (current: any, prefix = '', depth = 0) => {
      if (depth > maxDepth || typeof current !== 'object' || current === null) {
        return;
      }

      Object.entries(current).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        paths.push(path);

        if (Array.isArray(value)) {
          value.forEach((item, idx) => {
            const arrayPath = `${path}[${idx}]`;
            paths.push(arrayPath);
            if (typeof item === 'object' && item !== null) {
              traverse(item, arrayPath, depth + 1);
            }
          });
        } else if (typeof value === 'object') {
          traverse(value, path, depth + 1);
        }
      });
    };

    traverse(obj);
    return paths;
  },

  deletePath: (obj: any, path: string): void => {
    const keys = parsePath(path);
    const lastKey = keys.pop();
    if (!lastKey) return;

    const target = keys.reduce((current, key) => current?.[key], obj);
    if (target && lastKey in target) {
      delete target[lastKey];
    }
  },
}));
